import React from "react";
import { useNavigate } from "react-router-dom";
import PublicNavbar from "../components/PublicNavbar";
import Footer from "../components/Footer";
import { ArrowLeft, Loader2, Mail } from "lucide-react";

const colors = {
  primary: "#03f8d7",
  accent: "#ff2d75",
  deepBg: "#060608",
  surface: "rgba(13, 13, 18, 0.95)",
  textMain: "#ffffff",
  textMuted: "#94a3b8",
  border: "rgba(255, 255, 255, 0.08)",
};

function PendingApproval() {
  const navigate = useNavigate();
  const supportEmail = "support@maritimesupplysuite.shop";

  const handleContactSupport = () => {
    window.location.href = `mailto:${supportEmail}?subject=Node Clearance Status - Maritime Supply Suite`;
  };

  return (
    <div className="pending-page">
      <style>
        {`
          .pending-page {
            min-height: 100vh;
            background-color: ${colors.deepBg};
            display: flex;
            flex-direction: column;
            font-family: 'Inter', sans-serif;
            color: ${colors.textMain};
          }

          .content-wrapper {
            flex: 1;
            display: flex;
            flex-direction: column;
            align-items: center;
            padding: 60px 20px 80px 20px; 
            box-sizing: border-box;
          }

          .pending-card {
            width: 100%;
            max-width: 480px;
            background: ${colors.surface};
            padding: 40px 24px;
            border-radius: 24px;
            border: 1px solid ${colors.border};
            box-shadow: 0 40px 100px rgba(3, 248, 215, 0.15);
            text-align: center;
            position: relative;
            overflow: hidden;
            backdrop-filter: blur(10px);
            margin-top: 20px; 
          }

          .scanning-line {
            position: absolute;
            top: 0; left: 0; right: 0;
            height: 2px;
            background: linear-gradient(90deg, transparent, ${colors.primary}, transparent);
            box-shadow: 0 0 15px ${colors.primary};
            z-index: 3;
            animation: scan 3s infinite linear;
          }

          @keyframes scan {
            0% { top: 0; opacity: 0; }
            50% { opacity: 1; }
            100% { top: 100%; opacity: 0; }
          }

          .status-icon-box {
            width: 70px;
            height: 70px;
            background: rgba(3, 248, 215, 0.05);
            border: 1px solid rgba(3, 248, 215, 0.2);
            border-radius: 18px;
            display: flex;
            align-items: center;
            justify-content: center;
            margin: 0 auto 25px;
            color: ${colors.primary};
          }

          .pending-title {
            font-size: 22px;
            font-weight: 900;
            letter-spacing: 2px;
            margin-bottom: 16px;
            text-transform: uppercase;
          }

          .pending-message {
            font-size: 14px;
            color: ${colors.textMuted};
            line-height: 1.6;
            margin-bottom: 30px;
          }

          .audit-box {
            background: rgba(0, 0, 0, 0.3);
            border: 1px solid ${colors.border};
            border-left: 4px solid ${colors.primary};
            padding: 20px;
            border-radius: 12px;
            text-align: left;
            margin-bottom: 30px;
          }

          .audit-title {
            font-size: 11px;
            font-weight: 800;
            text-transform: uppercase;
            letter-spacing: 1.5px;
            color: ${colors.primary};
            margin-bottom: 12px;
          }

          .audit-list {
            display: flex;
            flex-direction: column;
            gap: 10px;
          }

          .audit-item {
            font-size: 13px;
            color: #e2e8f0;
            display: flex;
            align-items: center;
            gap: 12px;
          }

          .dot {
            min-width: 6px;
            height: 6px;
            background: ${colors.primary};
            border-radius: 50%;
            box-shadow: 0 0 8px ${colors.primary};
          }

          .action-btn {
            width: 100%;
            padding: 14px;
            background: rgba(255, 255, 255, 0.03);
            color: #fff;
            border: 1px solid ${colors.border};
            border-radius: 12px;
            font-weight: 700;
            text-transform: uppercase;
            letter-spacing: 1px;
            cursor: pointer;
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 10px;
            transition: all 0.3s ease;
            font-size: 13px;
          }

          .action-btn:hover {
            background: rgba(255, 255, 255, 0.08);
            border-color: ${colors.textMuted};
          }

          .footer-note {
            margin-top: 25px;
            font-size: 13px;
            color: ${colors.textMuted};
          }

          .admin-link {
            color: ${colors.primary};
            font-weight: 700;
            cursor: pointer;
            display: inline-flex;
            align-items: center;
            gap: 5px;
            margin-top: 5px;
          }

          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
          .animate-spin {
            animation: spin 1s linear infinite;
          }

          @media (max-width: 480px) {
            .content-wrapper {
              padding: 40px 15px 60px 15px;
            }
            .pending-card {
              padding: 30px 20px;
              margin-top: 10px;
            }
            .pending-title { font-size: 18px; }
          }
        `}
      </style>

      <PublicNavbar />

      <div className="content-wrapper">
        <div className="pending-card">
          <div className="scanning-line" />

          <div className="status-icon-box">
            <Loader2 className="animate-spin" size={28} />
          </div>

          <h1 className="pending-title">Station Clearance Pending</h1>

          <p className="pending-message">
            Access to the <span style={{ color: colors.primary, fontWeight: '700' }}>Maritime Supply Suite</span> is restricted. 
            Your station is undergoing a mandatory <span style={{ color: '#fff', fontWeight: '600' }}>Operational Clearance Audit</span>.
          </p>

          <div className="audit-box">
            <div className="audit-title">Protocol Sequence</div>
            <div className="audit-list">
              <div className="audit-item"><div className="dot" /> Neural link identity authentication.</div>
              <div className="audit-item"><div className="dot" /> Logistics credential cross-reference.</div>
              <div className="audit-item"><div className="dot" /> Synchronization with global registry.</div>
            </div>
          </div>

          <button className="action-btn" onClick={() => navigate("/login")}>
            <ArrowLeft size={16} />
            Return to Login
          </button>

          <div className="footer-note">
            <div>Need urgent access?</div>
            <div className="admin-link" onClick={handleContactSupport}>
              <Mail size={14} /> Request Admin Authorization
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}

export default PendingApproval;